import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightFromLine, SearchIcon, CheckCircle2Icon, TruckIcon, UserIcon, CalendarIcon, HashIcon, XIcon } from 'lucide-react';
interface VehicleEntry {
  gatePassNo: string;
  vehicleNo: string;
  vehicleType: string;
  user: string;
  dateTime: string;
}
const vehicleRecords: VehicleEntry[] = [{
  gatePassNo: 'GP-2026-0001',
  vehicleNo: 'MH-12-AB-1234',
  vehicleType: 'Truck',
  user: 'Rajesh Kumar',
  dateTime: '2026-04-15 08:30 AM'
}, {
  gatePassNo: 'GP-2026-0002',
  vehicleNo: 'MH-14-CD-5678',
  vehicleType: 'Car',
  user: 'Priya Sharma',
  dateTime: '2026-04-15 09:15 AM'
}, {
  gatePassNo: 'GP-2026-0003',
  vehicleNo: 'GJ-05-EF-9012',
  vehicleType: 'Van',
  user: 'Amit Patel',
  dateTime: '2026-04-15 09:45 AM'
}, {
  gatePassNo: 'GP-2026-0004',
  vehicleNo: 'DL-01-GH-3456',
  vehicleType: 'Trailer',
  user: 'Sunil Verma',
  dateTime: '2026-04-15 10:00 AM'
}, {
  gatePassNo: 'GP-2026-0005',
  vehicleNo: 'RJ-14-IJ-7890',
  vehicleType: 'Truck',
  user: 'Deepak Singh',
  dateTime: '2026-04-15 10:30 AM'
}, {
  gatePassNo: 'GP-2026-0006',
  vehicleNo: 'KA-01-KL-2345',
  vehicleType: 'Car',
  user: 'Meena Reddy',
  dateTime: '2026-04-15 11:00 AM'
}, {
  gatePassNo: 'GP-2026-0007',
  vehicleNo: 'TN-09-MN-6789',
  vehicleType: 'Bike',
  user: 'Karthik Nair',
  dateTime: '2026-04-15 11:20 AM'
}, {
  gatePassNo: 'GP-2026-0008',
  vehicleNo: 'UP-32-OP-0123',
  vehicleType: 'Truck',
  user: 'Vikram Yadav',
  dateTime: '2026-04-15 11:45 AM'
}, {
  gatePassNo: 'GP-2026-0009',
  vehicleNo: 'MP-09-QR-4567',
  vehicleType: 'Van',
  user: 'Anita Gupta',
  dateTime: '2026-04-15 12:10 PM'
}, {
  gatePassNo: 'GP-2026-0010',
  vehicleNo: 'HR-26-ST-8901',
  vehicleType: 'Trailer',
  user: 'Rohit Malik',
  dateTime: '2026-04-15 12:30 PM'
}];
const vehicleTypeBadge: Record<string, {
  bg: string;
  text: string;
}> = {
  Truck: {
    bg: 'bg-amber-50 border-amber-200',
    text: 'text-amber-700'
  },
  Car: {
    bg: 'bg-blue-50 border-blue-200',
    text: 'text-blue-700'
  },
  Bike: {
    bg: 'bg-green-50 border-green-200',
    text: 'text-green-700'
  },
  Van: {
    bg: 'bg-purple-50 border-purple-200',
    text: 'text-purple-700'
  },
  Trailer: {
    bg: 'bg-rose-50 border-rose-200',
    text: 'text-rose-700'
  }
};
export function VehicleFinalExitPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<VehicleEntry | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [exitConfirmed, setExitConfirmed] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    const q = searchQuery.trim().toUpperCase();
    const found = vehicleRecords.find(r => r.gatePassNo.toUpperCase() === q || r.vehicleNo.toUpperCase() === q);
    setSearchResult(found || null);
    setNotFound(!found);
    setExitConfirmed(false);
    setHasSearched(true);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const handleConfirmExit = () => {
    setExitConfirmed(true);
  };
  const handleReset = () => {
    setSearchQuery('');
    setSearchResult(null);
    setNotFound(false);
    setExitConfirmed(false);
    setHasSearched(false);
  };
  const now = new Date();
  const exitTime = now.toLocaleString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  return <div className="w-full max-w-4xl mx-auto" data-id="element-1612">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-1613">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6" data-id="element-1614">
          <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-1615">
            <ArrowRightFromLine className="w-6 h-6 text-brand-teal" data-id="element-1616" />
          </div>
          <div data-id="element-1617">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-1618">
              Vehicle Final Exit
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1619">
              Search by gate pass number or vehicle number to process exit.
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 mb-6" data-id="element-1620">
          <div className="relative flex-1" data-id="element-1621">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 pointer-events-none" data-id="element-1622" />
            <input type="text" placeholder="Enter Gate Pass No. or Vehicle No. (e.g. GP-2026-0001)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={handleKeyDown} className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal transition-all" data-id="element-1623" />
          </div>
          <button onClick={handleSearch} className="px-5 py-3 bg-brand-teal text-white rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors whitespace-nowrap" data-id="element-1624">
            Search
          </button>
        </div>

        {/* Not Found State */}
        <AnimatePresence mode="wait" data-id="element-1625">
          {hasSearched && notFound && <motion.div key="not-found" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="border border-red-200 bg-red-50 rounded-xl p-6 text-center" data-id="element-1626">
              <div className="text-red-400 mb-2" data-id="element-1627">
                <XIcon className="w-10 h-10 mx-auto" data-id="element-1628" />
              </div>
              <p className="text-red-700 font-semibold text-sm" data-id="element-1629">
                No record found
              </p>
              <p className="text-red-500 text-xs mt-1" data-id="element-1630">
                Please check the gate pass number or vehicle number and try
                again.
              </p>
            </motion.div>}

          {/* Search Result */}
          {searchResult && !exitConfirmed && <motion.div key="result" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1631">
              <div className="bg-slate-50 px-5 py-3 border-b border-slate-200" data-id="element-1632">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1633">
                  Vehicle Found — Confirm Exit
                </p>
              </div>
              <div className="p-5 space-y-4" data-id="element-1634">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1635">
                  <div className="flex items-start gap-3" data-id="element-1636">
                    <div className="bg-brand-teal/10 p-2 rounded-lg" data-id="element-1637">
                      <HashIcon className="w-4 h-4 text-brand-teal" data-id="element-1638" />
                    </div>
                    <div data-id="element-1639">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-1640">
                        Gate Pass No.
                      </p>
                      <p className="text-sm font-bold text-slate-800 font-mono" data-id="element-1641">
                        {searchResult.gatePassNo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1642">
                    <div className="bg-blue-50 p-2 rounded-lg" data-id="element-1643">
                      <TruckIcon className="w-4 h-4 text-blue-600" data-id="element-1644" />
                    </div>
                    <div data-id="element-1645">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-1646">
                        Vehicle No.
                      </p>
                      <p className="text-sm font-bold text-slate-800 font-mono" data-id="element-1647">
                        {searchResult.vehicleNo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1648">
                    <div className="bg-amber-50 p-2 rounded-lg" data-id="element-1649">
                      <TruckIcon className="w-4 h-4 text-amber-600" data-id="element-1650" />
                    </div>
                    <div data-id="element-1651">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-1652">
                        Vehicle Type
                      </p>
                      {(() => {
                    const badge = vehicleTypeBadge[searchResult.vehicleType];
                    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium border mt-0.5 ${badge?.bg || ''} ${badge?.text || ''}`} data-id="element-1653">
                            {searchResult.vehicleType}
                          </span>;
                  })()}
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1654">
                    <div className="bg-purple-50 p-2 rounded-lg" data-id="element-1655">
                      <UserIcon className="w-4 h-4 text-purple-600" data-id="element-1656" />
                    </div>
                    <div data-id="element-1657">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-1658">User</p>
                      <p className="text-sm font-semibold text-slate-700" data-id="element-1659">
                        {searchResult.user}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1660">
                    <div className="bg-green-50 p-2 rounded-lg" data-id="element-1661">
                      <CalendarIcon className="w-4 h-4 text-green-600" data-id="element-1662" />
                    </div>
                    <div data-id="element-1663">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-1664">
                        Entry Date & Time
                      </p>
                      <p className="text-sm text-slate-700" data-id="element-1665">
                        {searchResult.dateTime}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1666">
                    <div className="bg-rose-50 p-2 rounded-lg" data-id="element-1667">
                      <ArrowRightFromLine className="w-4 h-4 text-rose-600" data-id="element-1668" />
                    </div>
                    <div data-id="element-1669">
                      <p className="text-xs text-slate-400 font-medium" data-id="element-1670">
                        Exit Time
                      </p>
                      <p className="text-sm font-semibold text-slate-700" data-id="element-1671">
                        {exitTime}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-1672">
                  <button onClick={handleConfirmExit} className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-4 py-3 rounded-xl font-medium text-sm hover:bg-brand-tealHover transition-colors" data-id="element-1673">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-1674" />
                    Confirm Exit
                  </button>
                  <button onClick={handleReset} className="inline-flex items-center justify-center gap-2 bg-white text-slate-600 px-4 py-3 rounded-xl font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-1675">
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>}

          {/* Exit Confirmed */}
          {exitConfirmed && searchResult && <motion.div key="confirmed" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="border border-green-200 bg-green-50 rounded-xl p-8 text-center" data-id="element-1676">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            bounce: 0.5,
            delay: 0.1
          }} data-id="element-1677">
                <CheckCircle2Icon className="w-14 h-14 text-green-500 mx-auto mb-3" data-id="element-1678" />
              </motion.div>
              <h3 className="text-lg font-bold text-green-800 mb-1" data-id="element-1679">
                Exit Processed Successfully
              </h3>
              <p className="text-sm text-green-600 mb-1" data-id="element-1680">
                Gate Pass{' '}
                <span className="font-mono font-bold" data-id="element-1681">
                  {searchResult.gatePassNo}
                </span>{' '}
                — Vehicle{' '}
                <span className="font-mono font-bold" data-id="element-1682">
                  {searchResult.vehicleNo}
                </span>
              </p>
              <p className="text-xs text-green-500" data-id="element-1683">
                Exit recorded at {exitTime}
              </p>
              <button onClick={handleReset} className="mt-5 inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-xl font-medium text-sm hover:bg-green-700 transition-colors" data-id="element-1684">
                Process Another Exit
              </button>
            </motion.div>}

          {/* Empty State */}
          {!hasSearched && <motion.div key="empty" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50" data-id="element-1685">
              <SearchIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-1686" />
              <p className="text-slate-500 text-sm font-medium" data-id="element-1687">
                Search for a gate pass to begin
              </p>
              <p className="text-slate-400 text-xs mt-1" data-id="element-1688">
                Enter a gate pass number or vehicle number above
              </p>
            </motion.div>}
        </AnimatePresence>
      </motion.div>
    </div>;
}